/**
 * Group products by organization
 * @param {Array} orgs - Organization list
 * @param {Array} products - Ordered products
 * @param {Array} lastWeekProducts - Last week's products
 * @param {Array} previousDayProducts - Previous day's products
 * @param {Object} orders - Orders data
 * @param {number} orgCommentPropId - Property ID for org comments
 * @param {Function} getAggregatedOrders - Function to aggregate orders
 * @returns {Array} Products grouped by organization
 */
export function groupProductsByOrganization(
  orgs,
  products,
  lastWeekProducts,
  previousDayProducts,
  orders,
  orgCommentPropId,
  getAggregatedOrders,
) {
  return orgs
    .map((org) => {
      const orderComments = orders.orders
        .filter((z) => z.payerId === org.id)
        .filter((z) => z.comment)
        .map((z) => z.comment)
        .join('\n');

      return {
        ...org,
        ordersComment: orderComments,
        comment: org.properties[orgCommentPropId] || '',
        orders: getAggregatedOrders(
          products.filter((z) => z.payerId === org.id),
          lastWeekProducts.filter((z) => z.payerId === org.id),
          previousDayProducts.filter((z) => z.payerId === org.id),
        ),
      };
    })
    .filter((x) => x.orders.length > 0);
}

/**
 * Get products by organization or ungrouped
 * @param {boolean} groupByOrg - Whether to group by organization
 * @param {Array} products - All ordered products
 * @param {Array} lastWeekProducts - Last week's products
 * @param {Array} previousDayProducts - Previous day's products
 * @param {Array} orgs - Organizations
 * @param {Object} orders - Orders data
 * @param {number} orgCommentPropId - Property ID for org comments
 * @param {Function} getAggregatedOrders - Function to aggregate orders
 * @param {Array} selectedGroupFilter - Selected group codes to filter by
 * @returns {Array} Grouped or ungrouped products
 */
export function getProductsByOrgView(
  groupByOrg,
  products,
  lastWeekProducts,
  previousDayProducts,
  orgs,
  orders,
  orgCommentPropId,
  getAggregatedOrders,
  selectedGroupFilter = [],
) {
  if (!groupByOrg) {
    return [
      {
        name: '',
        orders: getAggregatedOrders(products, lastWeekProducts, previousDayProducts),
      },
    ];
  }

  // Group products by organization first
  const orgGrouped = groupProductsByOrganization(
    orgs,
    products,
    lastWeekProducts,
    previousDayProducts,
    orders,
    orgCommentPropId,
    getAggregatedOrders,
  );

  // If there's a group filter, find products that don't match any organization
  if (selectedGroupFilter.length > 0) {
    const orgIds = new Set(orgGrouped.map((org) => org.id));

    // Find products that belong to selected groups but no organization has them
    const unmatchedProducts = products.filter((product) => {
      const payerGroups = product.payerGroups || [];
      const hasSelectedGroup = selectedGroupFilter.some((group) => payerGroups.includes(group));
      const belongsToOrg = orgIds.has(product.payerId);
      return hasSelectedGroup && !belongsToOrg;
    });

    const unmatchedLastWeekProducts = lastWeekProducts.filter((product) => {
      const payerGroups = product.payerGroups || [];
      const hasSelectedGroup = selectedGroupFilter.some((group) => payerGroups.includes(group));
      const belongsToOrg = orgIds.has(product.payerId);
      return hasSelectedGroup && !belongsToOrg;
    });

    const unmatchedPreviousDayProducts = previousDayProducts.filter((product) => {
      const payerGroups = product.payerGroups || [];
      const hasSelectedGroup = selectedGroupFilter.some((group) => payerGroups.includes(group));
      const belongsToOrg = orgIds.has(product.payerId);
      return hasSelectedGroup && !belongsToOrg;
    });

    // Create Campus Bemke organization for unmatched products
    const campusBemkeOrders = getAggregatedOrders(
      unmatchedProducts,
      unmatchedLastWeekProducts,
      unmatchedPreviousDayProducts,
    );

    if (campusBemkeOrders.length > 0) {
      const campusBemkeOrderComments = orders.orders
        .filter((z) => unmatchedProducts.some((p) => p.orderId === z.id))
        .filter((z) => z.comment)
        .map((z) => z.comment)
        .join('\n');

      orgGrouped.push({
        id: 'campus-bemke-virtual',
        name: 'Campus Bemke',
        type: 'Organization',
        properties: {},
        ordersComment: campusBemkeOrderComments,
        comment: '',
        orders: campusBemkeOrders,
      });
    }
  }

  return orgGrouped;
}
